import React, { useState } from 'react';
import { Card, Button, Radio, Typography, Timeline, Badge, Spin, message } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface StepContentProps {
  current: number;
  onNext: () => void;
  onPrev: () => void;
}

interface MemberRegistrationProgressProps extends StepContentProps {
  // You might want to pass some initial data or status here
  // For now, we'll simulate status selection
}

const MemberRegistrationProgress: React.FC<MemberRegistrationProgressProps> = ({ onNext: _onNext, onPrev }) => {
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [selectedStatus, setSelectedStatus] = useState<'pending' | 'success' | 'failed'>('pending'); // 仅记录选择状态
  const [timelineCurrent, setTimelineCurrent] = useState(2); // 0: initial, 1: submitted, 2: processing, 3: result
  const [failureReasonsSelected, setFailureReasonsSelected] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);

  // 失败原因列表
  const failureReasons = [
    '企业信息与工商登记不符',
    '法人身份信息验证失败',
    '企业经营状态异常或已注销',
    '企业存在不良信用记录',
    '提交的材料不清晰或信息不完整',
    '企业注册资本不符合要求'
  ];

  const handleViewProgress = async () => {
    // 点击查询时仅转圈
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500)); // 模拟查询延迟

    if (selectedStatus === 'pending') {
      // 审核中：不改变节点结构，只保证在审核中节点
      setStatus('pending');
      setTimelineCurrent(2);
      setFailureReasonsSelected([]);
      setLoading(false);
      return;
    }

    if (selectedStatus === 'failed') {
      // 随机选取多条失败原因
      const pool = [...failureReasons];
      const count = Math.min(3, Math.max(2, Math.floor(Math.random() * 3) + 1)); // 2-3 条
      const picked: string[] = [];
      while (picked.length < count && pool.length > 0) {
        const idx = Math.floor(Math.random() * pool.length);
        picked.push(pool.splice(idx, 1)[0]);
      }
      setStatus('failed');
      setTimelineCurrent(3);
      setFailureReasonsSelected(picked);
      setLoading(false);
      return;
    }

    if (selectedStatus === 'success') {
      // 成功：不更新当前页面状态与时间轴，仅提示并跳转
      setLoading(false);
      if (typeof window !== 'undefined') {
        // 使用 antd message 提示，如未引入可按需替换
        // 这里保留 message.success，如项目未配置可改为其他提示实现
        // @ts-ignore
        message && message.success && message.success('会员开立成功，请继续完成登记簿开立');
      }
      setTimeout(() => {
        _onNext(); // Use _onNext to go to the next step (LedgerRegistrationStep)
      }, 600);
      return;
    }
  };





  const getStatusDescription = () => {
    switch (status) {
      case 'success':
        return '您的会员开立申请已审核通过，可以继续进行后续操作。';
      case 'failed':
        return '您的会员开立申请未能通过审核，请根据失败原因修改资料。';
      default:
        return '您的会员开立申请正在审核中，请耐心等待。';
    }
  };

  return (
    <Card
      className="enterprise-step-card enterprise-fade-in"
      style={{ width: '800px', margin: '24px auto 0 auto' }}

    >

      <div style={{ padding: '24px', position: 'relative' }}>
        <Spin spinning={loading}>
        {/* 状态概览（对齐登记簿进度页样式） */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Badge
            count={status === 'success' ? '开户成功' : status === 'failed' ? '开户失败' : '审核中'}
            style={{
              backgroundColor: status === 'success' ? '#52c41a' : status === 'failed' ? '#ff4d4f' : '#1890ff',
              fontSize: '14px',
              padding: '4px 12px'
            }}
          >
            <div
              style={{
                fontSize: '48px',
                color: status === 'success' ? '#52c41a' : status === 'failed' ? '#ff4d4f' : '#1890ff',
                marginBottom: 16
              }}
            >
              {status === 'success' ? <CheckCircleOutlined /> : status === 'failed' ? <CloseCircleOutlined /> : <ClockCircleOutlined />}
            </div>
          </Badge>
          <Title level={4} style={{ marginBottom: 0 }}>{getStatusDescription()}</Title>
          {status === 'pending' && <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>预计审核时长2~3小时，具体以银行审核为准</Text>}
        </div>
        {/* Floating Status Selector - 参考登记簿扫码授权页面的专业设计 */}
        <div style={{ position: 'fixed', top: 120, right: 20, zIndex: 1000, background: 'rgba(255, 255, 255, 0.9)', padding: '12px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', border: '1px solid #d9d9d9' }}>
          <div style={{marginBottom: 8, fontWeight: 600}}>状态切换（演示）</div>
          <Radio.Group
            value={selectedStatus}
            onChange={(e: any) => {
              setSelectedStatus(e.target.value);
              // 仅记录意向状态，不立即变更展示
            }}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="pending">📊 审核中</Radio.Button>
            <Radio.Button value="success">✅ 开户成功</Radio.Button>
            <Radio.Button value="failed">❌ 开户失败</Radio.Button>
          </Radio.Group>
        </div>

          <div style={{ maxWidth: 600, margin: '48px auto 0' }}>
            <Timeline
              items={[
                {
                  dot: timelineCurrent >= 1 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />,
                  color: timelineCurrent >= 1 ? 'green' : 'blue',
                  children: (
                    <> 
                      <Text strong>会员资料提料</Text>
                      <Text type="secondary" style={{ display: 'block' }}>会员资料已提交</Text>
                    </>
                  ),
                },
                {
                  dot: timelineCurrent >= 2 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />,
                  color: timelineCurrent >= 2 ? 'green' : 'blue',
                  children: (
                    <> 
                      <Text strong>审核中</Text>
                      <Text type="secondary" style={{ display: 'block' }}>正在审核您的会员开立申请</Text>
                    </>
                  ),
                },
                {
                  dot: timelineCurrent >= 3 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />,
                  color: timelineCurrent >= 3 ? 'green' : 'blue',
                  children: (
                    <> 
                      <Text strong>开立结果通知</Text>
                      {status === 'failed' ? (
                        <div style={{ marginTop: 8 }}>
                          <Text type="danger" style={{ display: 'block', marginBottom: 4 }}>失败原因：</Text>
                          <ul style={{ paddingLeft: 18, margin: 0 }}>
                            {(failureReasonsSelected.length ? failureReasonsSelected : ['系统判定未通过']).map((r, i) => (
                              <li key={i} style={{ color: '#ff4d4f' }}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      ) : status === 'success' ? (
                        <Text type="success" style={{ display: 'block' }}>审核通过，可以继续绑卡</Text>
                      ) : (
                        <Text type="secondary" style={{ display: 'block' }}>等待最终审核结果</Text>
                      )}
                    </>
                  ),
                },
              ]}
            />
            
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              {status !== 'failed' && (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleViewProgress}
                  icon={<ReloadOutlined />}
                  style={{ minWidth: 140 }}
                >
                  查询审核进度
                </Button>
              )}
              {(status === 'pending' || status === 'failed') && (
                <Button
                  size="large"
                  style={{ marginLeft: 12, minWidth: 140 }}
                  onClick={() => window.open('about:blank', '_blank')}
                >
                  返回订单中心
                </Button>
              )}
              {status === 'failed' && (
                <Button
                  type="primary"
                  size="large"
                  style={{ marginLeft: 12, minWidth: 140 }}
                  onClick={() => onPrev()}
                >
                  重新开立会员
                </Button>
              )}
            </div>






          </div>
        </Spin>
      </div>
    </Card>
  );
};

export default MemberRegistrationProgress;
